/**
 * Clear a stale MSAL "interaction in progress" flag on page load (13.1-13 follow-up, ISS-240).
 *
 * MSAL keeps `msal.interaction.status` in sessionStorage. That storage belongs to the tab and survives a reload. A
 * popup that died (before 13.1-13, parallel popups did) leaves the flag set. Every later sign-in in that tab then
 * throws `interaction_in_progress`, and a refresh never clears it; only a new tab or a private window works.
 *
 * A page that is loading cannot have an interaction of its own in progress, with three exceptions:
 *   - the redirect return, whose URL carries the auth response (`code=` / `error=`), which handleRedirectPromise
 *     must see together with the flag;
 *   - the redirect return through the MSAL v5 bridge (ISS-295): redirect.html moves the response into sessionStorage
 *     (`msal.<clientId>.urlHash`) and navigates back with a clean URL. Clearing the flag there would make
 *     handleRedirectPromise ignore the response, and the sign-in would loop back to the sign-in page;
 *   - the popup window itself (it has an opener), which MSAL's opener reads.
 * All three leave the flag alone. Called in main.tsx before `initialize()`.
 */

export const STATUS_KEY = 'msal.interaction.status';

interface FlagStorage {
  getItem(key: string): string | null;
  removeItem(key: string): void;
  readonly length: number;
  key(index: number): string | null;
}

const AUTH_RESPONSE = /(^|[#?&])(code|error)=/;
/** Where the v5 redirect bridge leaves the auth response for handleRedirectPromise. */
const BRIDGED_RESPONSE = /^msal\.[^.]+\.urlHash$/;

function hasBridgedResponse(storage: FlagStorage): boolean {
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key !== null && BRIDGED_RESPONSE.test(key)) return true;
  }
  return false;
}

/** Returns true when a leftover flag was removed. Never throws. */
export function clearStaleInteraction(
  storage: FlagStorage,
  location: { hash: string; search: string },
  hasOpener: boolean,
): boolean {
  if (hasOpener || AUTH_RESPONSE.test(location.hash) || AUTH_RESPONSE.test(location.search)) return false;
  try {
    if (storage.getItem(STATUS_KEY) === null) return false;
    if (hasBridgedResponse(storage)) return false;
    storage.removeItem(STATUS_KEY);
    return true;
  } catch {
    return false;
  }
}
